import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { comboboxRegistryContext } from './combobox-context.js'

/**
 * Groups declarative `<nord-combobox-option>` children under a heading inside a
 * [Combobox](/components/combobox/):
 *
 * ```html
 * <nord-combobox>
 *   <nord-combobox-group label="Frameworks">
 *     <nord-combobox-option value="next">Next.js</nord-combobox-option>
 *   </nord-combobox-group>
 * </nord-combobox>
 * ```
 *
 * Contained options inherit this `label` as their group. The element renders
 * nothing itself and is hidden with `display: none`.
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @slot - The group's `<nord-combobox-option>` children.
 */
@customElement('nord-combobox-group')
export default class ComboboxGroup extends LitElement {
  /** The group heading shown above its options. */
  @property({ reflect: true }) label = ''

  private registry = new ContextConsumer(this, {
    context: comboboxRegistryContext,
    subscribe: true,
  })

  protected createRenderRoot() {
    return this
  }

  protected updated() {
    // The heading changed — re-read so contained options pick up the new group.
    this.registry.value?.notify()
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-combobox-group': ComboboxGroup
  }
}
